<script>
  /**
   * Reusable Switch/Toggle Component
   * @prop {string} id - Switch ID
   * @prop {string} label - Label text
   * @prop {string} description - Description text below label
   * @prop {boolean} checked - Checked state (bindable)
   * @prop {boolean} disabled - Disabled state
   */
  let {
    id = "",
    label = "",
    description = "",
    checked = $bindable(false),
    disabled = false,
    onchange = undefined,
  } = $props();
</script>

<label class="switch-container" class:disabled for={id}>
  <input
    type="checkbox"
    {id}
    class="switch-input"
    bind:checked
    {disabled}
    {onchange}
  />
  <span class="switch-track">
    <span class="switch-thumb"></span>
  </span>
  {#if label || description}
    <span class="switch-content">
      {#if label}
        <span class="switch-label">{label}</span>
      {/if}
      {#if description}
        <span class="switch-description">{description}</span>
      {/if}
    </span>
  {/if}
</label>

<style>
  .switch-container {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    cursor: pointer;
    user-select: none;
  }

  .switch-container.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .switch-input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  .switch-track {
    position: relative;
    flex-shrink: 0;
    width: 48px;
    height: 26px;
    background: var(--color-gray-200);
    border-radius: 13px;
    transition:
      background-color var(--transition-fast),
      box-shadow var(--transition-fast);
    margin-top: 1px;
    border: 1px solid transparent;
  }

  .switch-input:checked + .switch-track {
    background: var(--gradient-primary);
    box-shadow: 0 2px 8px rgba(124, 58, 237, 0.3);
  }

  .switch-input:focus-visible + .switch-track {
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
  }

  .switch-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition:
      transform var(--transition-fast),
      box-shadow var(--transition-fast);
  }

  .switch-input:checked + .switch-track .switch-thumb {
    transform: translateX(22px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  }

  .switch-content {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .switch-label {
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-gray-700);
    line-height: 1.4;
  }

  .switch-description {
    font-size: var(--text-xs);
    color: var(--color-gray-500);
    line-height: 1.4;
  }
</style>
